import type { FC, SubmitEventHandler } from "react";
import { type ReadonlyURLSearchParams, useSearchParams } from "next/navigation";

import type { HttpError } from "@vita-learning/types";

import { Spinner } from "~/shared/components/loading/Spinner";
import { stringFieldsAreFilled, makeUrl } from "~/shared/utils";

import { PrimaryButton as Button } from "../../buttons/PrimaryButton";
import { SocialLogin } from "../../buttons/SocialLogin";
import { Link } from "../../interactives/Link";
import { BotInput } from "../../inputs/BotInput";
import { useTextInput } from "../../inputs/TextInput";

import {
	TextFormGroup,
	useTextFormGroup,
} from "../../formgroups/TextFormGroup";
import {
	usePasswordFormGroup,
	PasswordFormGroup,
} from "../../formgroups/PasswordFormGroup";

export interface LoginFormOnSubmitProps {
	readonly isEnabled: boolean;
	readonly email: string;
	readonly password: string;
}

interface LoginFormProps {
	readonly error: HttpError | null;
	readonly loginAttempts: number;
	readonly isLoading: boolean;
	readonly makeOnSubmit: (
		props: LoginFormOnSubmitProps
	) => SubmitEventHandler<HTMLFormElement>;
	readonly className?: string;
}

const makeForgotPasswordHref = (email: string): string => {
	const url = makeUrl("/forgot-password");

	if (!email) return url.pathname;

	url.searchParams.set("email", email);

	return url.pathname + url.search;
};

const makeRegisterHref = (
	searchParams: ReadonlyURLSearchParams | null
): string => {
	const url = makeUrl("/register");

	searchParams?.forEach((v, k) => url.searchParams.set(k, v));

	return url.pathname + url.search;
};

const REASONABLE_LOGIN_ATTEMPTS = 5;
const LOCKED_OUT_LOGIN_ATTEMPTS = 10;

export const LoginForm: FC<LoginFormProps> = ({
	isLoading,
	makeOnSubmit,
	loginAttempts,
	error,
	className,
}) => {
	const searchParams = useSearchParams();

	const botInput = useTextInput({
		label: "Honeypot",
	});

	const emailInput = useTextFormGroup({
		label: "Email",
		"data-testid": "login-email-error",
		type: "email",
		serverError: error,
		autoFocus: true,
	});

	const passwordInput = usePasswordFormGroup({
		label: "Password",
		"data-testid": "login-password-error",
		serverError: error,
		validate: () => {
			if (loginAttempts >= LOCKED_OUT_LOGIN_ATTEMPTS)
				return "Temporary lockout. Please try again later.";

			if (loginAttempts >= REASONABLE_LOGIN_ATTEMPTS) {
				if (!error)
					return "After multiple login attempts, we suggest you reset your password.";

				return (
					error.message +
					" again. It looks like you're unsure of your password. We suggest you reset it."
				);
			}

			return null;
		},
	});

	const isEnabled =
		stringFieldsAreFilled([emailInput.value, passwordInput.value]) &&
		loginAttempts < LOCKED_OUT_LOGIN_ATTEMPTS &&
		!botInput.value;

	return (
		<>
			<SocialLogin />
			<form
				onSubmit={makeOnSubmit({
					isEnabled,
					email: emailInput.value,
					password: passwordInput.value,
				})}
				className={className}
				data-testid="login-form"
			>
				<TextFormGroup {...emailInput} />
				<PasswordFormGroup {...passwordInput} />
				<BotInput {...botInput} />
				<Button
					className={`${className}__button`}
					disabled={!isEnabled}
					type="submit"
					data-testid="login-btn"
				>
					{isLoading ? <Spinner color="#FFF" /> : "Login"}
				</Button>
				<Link
					className={`${className}__link`}
					href={makeForgotPasswordHref(emailInput.value)}
					data-testid="forgot-password-link"
				>
					Forgot Password?
				</Link>
				<br />
				<Link
					className={`${className}__link`}
					href={makeRegisterHref(searchParams)}
					data-testid="register-link"
				>
					Don&apos;t have an account? Create Account
				</Link>
			</form>
		</>
	);
};

export const LoginFormFallback: FC<Pick<LoginFormProps, "className">> = ({
	className,
}) => {
	const email = useTextFormGroup({
		label: "Email",
		"data-testid": "login-email-error",
		type: "email",
		autoFocus: true,
	});

	const password = usePasswordFormGroup({
		label: "Password",
		"data-testid": "login-password-error",
		type: "password",
	});

	const fields = { email, password };

	return (
		<>
			<SocialLogin />
			<form className={className} data-testid="login-form">
				<TextFormGroup {...fields.email} />
				<PasswordFormGroup {...fields.password} />
				<BotInput />
				<Button
					className={`${className}__button`}
					disabled
					type="submit"
					data-testid="login-btn"
				>
					Login
				</Button>
				<Link
					className={`${className}__link`}
					href="/forgot-password"
					data-testid="forgot-password-link"
				>
					Forgot Password?
				</Link>
				<br />
				<Link
					className={`${className}__link`}
					href="/register"
					data-testid="register-link"
				>
					Don&apos;t have an account? Create Account
				</Link>
			</form>
		</>
	);
};
